import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { throttle } from 'lodash';
import { HighlightContext } from '../';
import type {
  HighlightContextT,
  HighlightDataT,
  OnCreateHighlightT,
  OnUpdateHighlightT,
  StoredHighlightDataT,
  TooltipPosDataT,
  UpdateStickerT,
} from './types';
import { HighlightColorsEnum, HtmlElAltIdEnum } from '../../enums';
import { setStoredQuestionNotePanelState } from './use-question-highlight-visibility.ts';

const getClosestElement = (target: EventTarget | null): HTMLElement | null => {
  if (target instanceof HTMLElement) return target;
  if (target instanceof Node) return target.parentElement;

  return null;
};

const getHighlightParentFromSelection = (
  selection: Selection | null,
): HTMLElement | null => {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const anchorNode =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;

  if (!(anchorNode instanceof HTMLElement)) return null;

  return anchorNode.closest(
    `[data-alt-type="${HtmlElAltIdEnum.HIGHLIGHT_PARENT}"]`,
  ) as HTMLElement | null;
};

const getSerializedHighlightData = (
  data: HighlightDataT[],
): StoredHighlightDataT[] =>
  data.map(({ sticker, ...highlight }) => ({
    ...highlight,
    sticker: {
      description: sticker.description ?? '',
      isNeedSticker: sticker.isNeedSticker,
    },
  }));

const hydrateHighlightData = (
  data: StoredHighlightDataT[],
): HighlightDataT[] =>
  data.map((highlight) => ({
    ...highlight,
    sticker: {
      ...highlight.sticker,
      title: document.body,
    },
  }));

const HighlightProvier = ({
  children,
  storageKey,
  initialData,
  onChange,
}: PropsWithChildren<{
  storageKey?: string;
  initialData?: StoredHighlightDataT[] | null;
  onChange?: (data: StoredHighlightDataT[]) => void;
}>): JSX.Element => {
  const [highlightStickersToggle, setHighlightStickersToggle] =
    useState<boolean>(false);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosDataT>(null);
  const [lastUsedColor, setLastUsedColor] = useState<HighlightColorsEnum>(
    HighlightColorsEnum.YELLOW,
  );

  const [highlightData, setHighlightData] = useState<HighlightDataT[]>([]);
  const [selectedHighlightKeyId, setSelectedHighlightKeyId] =
    useState<HighlightDataT['keyId']>('');

  const [highlightToggle, setHighlightToggle] = useState(false);
  const [hoveredHighlightKeyId, setHoveredHighlightKeyId] =
    useState<HighlightDataT['keyId']>('');

  const selectedHighLight = highlightData.find(
    (hl) => hl.keyId === selectedHighlightKeyId,
  );

  useEffect(() => {
    if (initialData) {
      setHighlightData(hydrateHighlightData(initialData));
      return;
    }

    if (storageKey && window?.localStorage) {
      try {
        const storedData = window.localStorage.getItem(storageKey);

        if (!storedData) {
          setHighlightData([]);
          return;
        }

        const parsedData = JSON.parse(storedData) as StoredHighlightDataT[];
        setHighlightData(hydrateHighlightData(parsedData));
        return;
      } catch {
        setHighlightData([]);
        return;
      }
    }

    setHighlightData([]);
  }, [initialData, storageKey]);

  useEffect(() => {
    const serializedData = getSerializedHighlightData(highlightData);

    if (storageKey && window?.localStorage) {
      window.localStorage.setItem(storageKey, JSON.stringify(serializedData));
    }

    onChange?.(serializedData);
  }, [highlightData, onChange, storageKey]);

  useEffect(() => {
    if (!highlightData.length) setHighlightStickersToggle(() => false);

    if (!highlightData.find((hl) => hl.sticker.isNeedSticker))
      setHighlightStickersToggle(() => false);
  }, [highlightData]);

  const onUpdateHighlight = useCallback(({
    sticker,
    ...data
  }: OnUpdateHighlightT): void => {
    if (data?.color) {
      setLastUsedColor(data.color);
    }

    setHighlightData((prevState) =>
      prevState.map((hl) => {
        if (hl.keyId === data.keyId) {
          return {
            ...hl,
            ...{ ...data, sticker: { ...hl.sticker, ...sticker } },
          };
        }

        return hl;
      }),
    );

    if (sticker?.isNeedSticker) {
      setHighlightStickersToggle(true);
      const currentQuestionId = highlightData.find(
        (highlight) => highlight.keyId === data.keyId,
      )?.questionId;

      if (currentQuestionId) {
        setStoredQuestionNotePanelState(currentQuestionId, true);
      }
    }
  }, [highlightData]);

  const onRemoveHighlight = useCallback((keyId: HighlightDataT['keyId']): void => {
    setHighlightData((prevState) =>
      prevState.filter((hl) => hl.keyId !== keyId),
    );
    setSelectedHighlightKeyId('');
    setTooltipPos(null);
  }, []);

  const onAddHighlight = useCallback((data: OnCreateHighlightT): void => {
    const color = data.color || lastUsedColor;

    if (data?.color) {
      setLastUsedColor(data.color);
    }

    setHighlightData((prevState) => [...prevState, { ...data, color }]);
    setSelectedHighlightKeyId(() => data.keyId);

    if (data.sticker.isNeedSticker) {
      setHighlightStickersToggle(true);
      setStoredQuestionNotePanelState(data.questionId, true);
    }
  }, [lastUsedColor]);

  const updateSticker = useCallback(({ keyId, sticker }: UpdateStickerT): void => {
    const questionId = highlightData.find(
      (hl) => hl.keyId === keyId,
    )!.questionId;

    setHighlightData((prevState) =>
      prevState.map((hl) => {
        if (hl.questionId !== questionId) return hl;

        if (hl.keyId === keyId) {
          return { ...hl, sticker: { ...hl.sticker, ...sticker } };
        }

        return hl;
      }),
    );
  }, [highlightData]);

  const handleMouseUp = useCallback((e: MouseEvent, options?: { isTouch?: boolean }) => {
    const selection = window.getSelection();

    const target = getClosestElement(e.target);

    const parentDiv =
      (target?.closest(
        `[data-alt-type="${HtmlElAltIdEnum.HIGHLIGHT_PARENT}"]`,
      ) as HTMLElement | null) ||
      (options?.isTouch ? getHighlightParentFromSelection(selection) : null);

    const tooltipDiv = target?.closest(
      `[data-alt-type="${HtmlElAltIdEnum.HIGHLIGHT_TOOLTIP}"]`,
    ) as HTMLElement | null;

    const clickedInsideTooltip = Boolean(tooltipDiv);

    if (!parentDiv && !clickedInsideTooltip) {
      setTooltipPos(null);
      setSelectedHighlightKeyId('');
      return;
    }

    if (parentDiv && !clickedInsideTooltip) {
      const isEmptySelectedContent =
        !selection || selection.rangeCount === 0 || selection.isCollapsed;

      if (!isEmptySelectedContent && highlightToggle) {
        if (options?.isTouch) {
          const highlightParentKey = parentDiv.getAttribute(
            'data-alt-parent-key',
          )!;

          setSelectedHighlightKeyId('');
          setTooltipPos({
            top: e.clientY + 50,
            left: e.clientX,
            highlightParentKey,
          });

          return;
        }

        setTooltipPos(null);
        setSelectedHighlightKeyId('');
        return;
      }

      if (isEmptySelectedContent) {
        const elementsUnderCursor = document.elementsFromPoint(
          e.clientX,
          e.clientY,
        );

        const selectedHighLight = elementsUnderCursor.find((el) =>
          el.getAttribute('data-highlight-keyid'),
        );

        if (selectedHighLight) {
          e.stopPropagation();

          const selectedHighLightKey = selectedHighLight.getAttribute(
            'data-highlight-keyid',
          )!;

          const highlightParentKey = selectedHighLight
            .closest(`[data-alt-type="${HtmlElAltIdEnum.HIGHLIGHT_PARENT}"]`)!
            .getAttribute(`data-alt-parent-key`)!;

          setSelectedHighlightKeyId(selectedHighLightKey);

          setTooltipPos({
            top: e.clientY + 50,
            left: e.clientX,
            highlightParentKey,
          });
        }

        if (!selectedHighLight) {
          setTooltipPos(null);
          setSelectedHighlightKeyId('');
        }

        return;
      }

      const highlightParentKey = parentDiv.getAttribute('data-alt-parent-key')!;

      setSelectedHighlightKeyId('');
      setTooltipPos({
        top: e.clientY + 50,
        left: e.clientX,
        highlightParentKey,
      });
    }
  }, [highlightToggle]);

  const throttledMouseMove = throttle((e: MouseEvent) => {
    const elementsUnderCursor = document.elementsFromPoint(
      e.clientX,
      e.clientY,
    );

    const highlightEl = elementsUnderCursor.find((el) =>
      el.hasAttribute('data-highlight-keyid'),
    ) as HTMLElement | undefined;

    if (highlightEl) {
      const hoveredHighlight = highlightEl.getAttribute(
        'data-highlight-keyid',
      )!;

      if (hoveredHighlight !== hoveredHighlightKeyId) {
        setHoveredHighlightKeyId(hoveredHighlight);
      }
    }

    if (!highlightEl) {
      if (hoveredHighlightKeyId) {
        setHoveredHighlightKeyId('');
      }
    }
  }, 100);

  useEffect(() => {
    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length > 0) return;
      const touch = e.changedTouches[0];

      if (!touch) return;

      const fakeMouseEvent = {
        clientX: touch.clientX,
        clientY: touch.clientY,
        target: e.target as EventTarget,
        stopPropagation: () => {},
      } as unknown as MouseEvent;

      handleMouseUp(fakeMouseEvent, { isTouch: true });
    };

    const throttledTouchMove = throttle((e: TouchEvent) => {
      const touch = e.touches[0];

      if (!touch) return;

      const fakeMouseEvent = {
        clientX: touch.clientX,
        clientY: touch.clientY,
      } as unknown as MouseEvent;

      throttledMouseMove(fakeMouseEvent);
    }, 100);

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', throttledMouseMove);

    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchmove', throttledTouchMove);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', throttledMouseMove);

      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', throttledTouchMove);

      throttledMouseMove.cancel();
      throttledTouchMove.cancel();
    };
  }, [highlightData, hoveredHighlightKeyId, throttledMouseMove, handleMouseUp]);

  const providerValue: HighlightContextT = useMemo(
    () => ({
      onAddHighlight,
      onRemoveHighlight,
      onUpdateHighlight,
      highlightData,
      selectedHighlightKeyId,
      setSelectedHighlightKeyId,
      lastUsedColor,
      tooltipPos,
      setTooltipPos,
      highlightStickersToggle,
      setHighlightStickersToggle,
      updateSticker,
      selectedHighLight,
      hoveredHighlightKeyId,
      highlightToggle,
      setHighlightToggle,
    }),
    [
      onAddHighlight,
      onRemoveHighlight,
      onUpdateHighlight,
      highlightToggle,
      setHighlightToggle,
      highlightData,
      selectedHighlightKeyId,
      setSelectedHighlightKeyId,
      lastUsedColor,
      tooltipPos,
      setTooltipPos,
      highlightStickersToggle,
      setHighlightStickersToggle,
      updateSticker,
      selectedHighLight,
      hoveredHighlightKeyId,
    ],
  );

  return (
    <HighlightContext.Provider value={providerValue}>
      {children}
    </HighlightContext.Provider>
  );
};

export default HighlightProvier;
